export const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
export const pick = (seed, arr) => arr[seed % arr.length];

const cityIntros = [
  (c, p) =>
    `${c.name} moves fast — and so do the risks that come with busy commercial districts, active development and round-the-clock operations across ${c.region}. Maze Security Solutions supplies licensed, uniformed security guards throughout ${c.name}, ${p.abbr}, backed by 24/7 dispatch and supervisors who verify every shift. From single-site static posts to multi-property mobile patrol programs, our ${c.name} teams are built around one standard: protection you can document.`,
  (c, p) =>
    `Businesses across ${c.region} trust Maze Security Solutions for one reason — we show up, on time, with licensed guards who know ${c.name}. Whether you manage a retail floor, a construction site, a residential tower or an industrial yard in ${c.name}, ${p.abbr}, our security programs combine trained people, verified patrols and clear reporting so you always know your property is protected.`,
  (c, p) =>
    `Security in ${c.name} is not a commodity — it is a commitment to the people and property that keep ${c.region} running. Maze Security Solutions deploys licensed security guards across ${c.name}, ${p.abbr} for commercial, residential, industrial and event environments. Every guard is provincially licensed, background-checked and supported by live dispatch and digital incident reporting.`,
  (c, p) =>
    `From downtown cores to suburban business parks, ${c.name} properties face theft, trespass and liability risks that cameras alone cannot solve. Maze Security Solutions provides professional security guard services across ${c.name}, ${p.abbr} — uniformed guards, mobile patrols, concierge teams and rapid alarm response — all managed under one accountable command structure serving ${c.region}.`,
];

const cityContext = [
  (c, p) =>
    `${p.note} In ${c.name}, our supervisors conduct unannounced site visits and our guards scan verified checkpoints on every patrol, giving property managers and owners time-stamped proof of coverage across ${c.region}.`,
  (c, p) =>
    `Operating in ${p.name} means operating under the ${p.act}. ${p.note} When you contract Maze Security in ${c.name}, you receive guard licence details, insurance documentation and post orders tailored to your site before the first shift begins.`,
  (c, p) =>
    `Every ${c.name} engagement starts with a walk-through of your property and an honest conversation about risk. ${p.note} That local, licence-first approach is why clients across ${c.region} renew with Maze Security year after year.`,
];

const cityCloser = [
  (c) =>
    `Need guards in ${c.name} tonight? Our dispatch line answers around the clock. Call +1 807 907-2874 or email info@mazesecurity.ca and a coordinator will scope your coverage the same day.`,
  (c) =>
    `Whether you need a single overnight guard or a full-site security program in ${c.name}, the fastest next step is a conversation. Reach dispatch at +1 807 907-2874 or info@mazesecurity.ca — no forms, no waiting.`,
  (c) =>
    `Maze Security Solutions can have licensed guards on your ${c.name} property quickly, with reporting you can verify from day one. Call +1 807 907-2874 or email info@mazesecurity.ca to start.`,
];

export const cityIntro = (c, p) => pick(hashStr(c.slug), cityIntros)(c, p);
export const cityLocalContext = (c, p) => pick(hashStr(c.slug + "ctx"), cityContext)(c, p);
export const cityCta = (c) => pick(hashStr(c.slug + "cta"), cityCloser)(c);

const provinceIntros = [
  (p) =>
    `${p.name} is home to some of Canada's busiest commercial corridors, industrial zones and fastest-growing communities — and every one of them needs dependable, licensed security. Maze Security Solutions provides professional security guard services across ${p.name}, from uniformed on-site guards and mobile patrols to fire watch, event security and 24/7 monitored response.`,
  (p) =>
    `From major urban centres to remote industrial sites, organizations across ${p.name} rely on Maze Security Solutions for licensed security guards, verified patrols and accountable reporting. Our ${p.name} operations are built on provincial licensing compliance, local supervision and a 24/7 dispatch line that always answers.`,
];

export const provinceIntro = (p) => pick(hashStr(p.slug), provinceIntros)(p);

const serviceIntros = [
  (s) =>
    `${s.angle} Maze Security Solutions delivers ${s.name.toLowerCase()} across every Canadian province and territory, with licensed personnel, supervised shifts and digital reporting on every engagement.`,
  (s) =>
    `${s.angle} Our ${s.name.toLowerCase()} program pairs provincially licensed guards with 24/7 dispatch support and time-stamped reporting — professional coverage anywhere in Canada.`,
];

export const serviceIntro = (s) => pick(hashStr(s.slug), serviceIntros)(s);

const serviceCityIntros = [
  (s, c, p) =>
    `${s.angle} In ${c.name}, ${p.abbr}, Maze Security Solutions provides ${s.name.toLowerCase()} delivered by provincially licensed guards, supervised on site and verified through digital checkpoint reporting.`,
  (s, c, p) =>
    `Looking for ${s.name.toLowerCase()} in ${c.name}? ${s.angle} Maze Security deploys licensed, uniformed personnel across ${c.region}, backed by 24/7 dispatch and reporting you can audit any time.`,
  (s, c, p) =>
    `${s.name} in ${c.name}, ${p.abbr} demands more than a warm body on post. ${s.angle} Our ${c.name} teams are licensed under ${p.name}'s regulatory framework and supported by field supervisors and live dispatch.`,
];

export const serviceCityIntro = (s, c, p) => pick(hashStr(s.slug + c.slug), serviceCityIntros)(s, c, p);

const serviceCityLocalVariants = [
  (s, c, p) =>
    `${p.note} For ${c.name} clients, that means ${s.name.toLowerCase()} you can verify: guard licence numbers on request, GPS-stamped patrol records, and incident reports in your inbox before the next business day.`,
  (s, c, p) =>
    `Every ${s.name.toLowerCase()} engagement in ${c.name} begins with a site walk-through and written post orders. ${p.note} Supervisors audit shifts without notice, so the coverage you contracted is the coverage you receive.`,
];

export const serviceCityLocal = (s, c, p) => pick(hashStr(c.slug + s.slug + "l"), serviceCityLocalVariants)(s, c, p);

export const cityFaqs = (c, p) => [
  {
    q: `Are your security guards in ${c.name} licensed?`,
    a: `Yes. Every guard Maze Security deploys in ${c.name} holds a valid licence issued under ${p.name}'s regulatory framework (${p.act}) and has passed provincial background screening. Licence details are available to clients on request.`,
  },
  {
    q: `How quickly can you deploy guards in ${c.name}?`,
    a: `Our 24/7 dispatch line coordinates new deployments across ${c.region}. Standard engagements begin with a site walk-through and written post orders; urgent coverage such as fire watch or alarm response can often be arranged the same day. Call +1 807 907-2874 to speak with dispatch.`,
  },
  {
    q: `Which security services do you offer in ${c.name}?`,
    a: `We offer our full service line in ${c.name}: uniformed guards, mobile patrol, construction site security, event security, concierge and front desk coverage, retail loss prevention, fire watch, alarm response, CCTV monitoring and more. Each program is tailored to your site after a walk-through.`,
  },
  {
    q: `How do I know the patrols actually happened?`,
    a: `Every patrol and shift in ${c.name} is verified with GPS-stamped checkpoint scans and digital incident reports. You receive time-stamped documentation of coverage — not verbal assurances.`,
  },
];

export const serviceFaqs = (s) => [
  {
    q: `What does your ${s.name.toLowerCase()} service include?`,
    a: `Our ${s.name.toLowerCase()} program includes ${s.deliverables[0].toLowerCase()}, ${s.deliverables[1].toLowerCase()}, and ${s.deliverables[3].toLowerCase()}, all documented with time-stamped digital reporting.`,
  },
  {
    q: `Are your guards licensed and insured?`,
    a: `Yes. All Maze Security personnel hold valid provincial security licences for the province they work in, pass background screening, and are covered by our commercial general liability insurance. Certificates are available to clients on request.`,
  },
  {
    q: `Which cities do you serve for ${s.name.toLowerCase()}?`,
    a: `We provide ${s.name.toLowerCase()} in more than 690 cities across all 13 Canadian provinces and territories, including Toronto, Montreal, Vancouver, Calgary, Edmonton, Ottawa, Winnipeg and Halifax.`,
  },
  {
    q: `How do I get started?`,
    a: `Call our 24/7 dispatch at +1 807 907-2874 or email info@mazesecurity.ca. A coordinator will discuss your site, arrange a walk-through and prepare written post orders before the first shift.`,
  },
];

export const serviceCityFaqs = (s, c, p) => [
  {
    q: `Do you provide ${s.name.toLowerCase()} in ${c.name}?`,
    a: `Yes. Maze Security Solutions provides ${s.name.toLowerCase()} throughout ${c.name} and ${c.region}, delivered by guards licensed under ${p.name}'s ${p.act}.`,
  },
  {
    q: `How is ${s.name.toLowerCase()} coverage verified in ${c.name}?`,
    a: `Every shift includes supervisor oversight, GPS-verified checkpoints where applicable, and time-stamped digital incident and activity reports delivered to your inbox.`,
  },
  {
    q: `How fast can you start ${s.name.toLowerCase()} in ${c.name}?`,
    a: `Urgent coverage can often be arranged the same day through our 24/7 dispatch. Standard engagements start with a site walk-through and written post orders. Call +1 807 907-2874 or email info@mazesecurity.ca.`,
  },
];

export const homeFaqs = [
  {
    q: "What areas of Canada do you serve?",
    a: "Maze Security Solutions serves all 13 Canadian provinces and territories — Ontario, Quebec, British Columbia, Alberta, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, Newfoundland and Labrador, Prince Edward Island, Northwest Territories, Yukon and Nunavut — covering more than 690 cities nationwide.",
  },
  {
    q: "Are your security guards licensed?",
    a: "Yes. Every guard we deploy holds a valid provincial or territorial security licence — for example under Ontario's PSISA, Quebec's Private Security Act (BSP) or British Columbia's Security Services Act — and passes background screening before their first shift.",
  },
  {
    q: "What security services does Maze Security offer?",
    a: "We provide 20 professional services including uniformed security guards, mobile patrol, construction site security, event security, concierge and front desk security, retail loss prevention, fire watch, alarm response and keyholding, CCTV monitoring, executive protection and security risk assessments.",
  },
  {
    q: "How do I request security services?",
    a: "Call our 24/7 dispatch line at +1 807 907-2874 or email info@mazesecurity.ca. A coordinator will discuss your requirements, arrange a site walk-through and prepare written post orders — no forms, no waiting.",
  },
  {
    q: "How is guard coverage verified?",
    a: "All shifts and patrols are supervised and verified with GPS-stamped checkpoints and time-stamped digital incident reports, so you receive documented proof of coverage after every shift.",
  },
];
